/**
 * Route tree. Two worlds: public access routes, and the authed layout that
 * carries authz + shell. The shell's Outlet renders the destination pages;
 * destinations a role may never open are absent from navigation AND guarded
 * here per route where a permission key applies.
 */

import {
  Link,
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
} from '@tanstack/react-router';
import { AuthedLayout, PublicOnly } from './AuthedLayout';
import { SignInPage } from '@/features/access/SignInPage';
import { ForgotPasswordPage } from '@/features/access/ForgotPasswordPage';
import { AccountPage } from '@/features/access/AccountPage';
import { ManualsPage } from '@/features/help/ManualsPage';
import { ChangePasswordPage } from '@/features/access/ChangePasswordPage';
import { TodayPage } from '@/features/today/TodayPage';
import { SETUP_SURFACES, SetupLayout } from '@/features/clinic-setup/SetupLayout';
import { ProfileTab } from '@/features/clinic-setup/ProfileTab';
import { StaffTab } from '@/features/clinic-setup/StaffTab';
import { StaffCreatePage } from '@/features/clinic-setup/StaffCreatePage';
import { MEMBER_TABS, MemberPage, type MemberSearch } from '@/features/clinic-setup/MemberPage';
import { RolesTab } from '@/features/clinic-setup/RolesTab';
import { HoursTab } from '@/features/clinic-setup/HoursTab';
import { RoomsTab } from '@/features/clinic-setup/RoomsTab';
import { ServicesTab } from '@/features/clinic-setup/ServicesTab';
import { CatalogsTab } from '@/features/clinic-setup/CatalogsTab';
import { ExtraChargesTab } from '@/features/clinic-setup/ExtraChargesTab';
import { PatientsListPage } from '@/features/patients/PatientsListPage';

import { ResolutionPreviewPage } from '@/features/patients/ResolutionPreviewPage';

import { lazy, Suspense } from 'react';
import type { CalendarSearch } from '@/features/scheduling/CalendarPage';
import type { DashboardSearch } from '@/features/reports/DashboardPage';
import { landingFor } from '@/features/reports/landing';
import type { FollowUpsSearch } from '@/features/followups/followUpRules';
import type { SessionReportsSearch } from '@/features/sessions/sessionReports';
import type { GroupNewSearch } from '@/features/groups/GroupSessionNewPage';

import type { PaymentsSearch } from '@/features/payments/PaymentsWorkspace';
import type { FamilySearch } from '@/features/family/FamilyWorkspacePage';

/** Heavy destinations load on demand — the main bundle stays the shell + foundations. */
function lazyPage<T extends Record<string, unknown>>(
  load: () => Promise<T>,
  name: keyof T & string,
): () => JSX.Element {
  const C = lazy(() => load().then((m) => ({ default: m[name] as React.ComponentType })));
  return function LazyPage(): JSX.Element {
    return (
      <Suspense fallback={<Skeleton height="120px" />}>
        <C />
      </Suspense>
    );
  };
}
const PatientRegisterPage = lazyPage(
  () => import('@/features/patients/PatientFormPage'),
  'PatientRegisterPage',
);
const PatientEditFormPage = lazyPage(
  () => import('@/features/patients/PatientFormPage'),
  'PatientEditFormPage',
);
const PatientCompleteRegistrationPage = lazyPage(
  () => import('@/features/patients/PatientFormPage'),
  'PatientCompleteRegistrationPage',
);
const PatientRecordPage = lazyPage(
  () => import('@/features/patients/PatientRecordPage'),
  'PatientRecordPage',
);
const IntakePage = lazyPage(() => import('@/features/intake/IntakePage'), 'IntakePage');
const AssessmentNewPage = lazyPage(
  () => import('@/features/assessments/AssessmentNewPage'),
  'AssessmentNewPage',
);
const AssessmentPage = lazyPage(
  () => import('@/features/assessments/AssessmentPage'),
  'AssessmentPage',
);
const PackageNewPage = lazyPage(
  () => import('@/features/packages/PackageNewPage'),
  'PackageNewPage',
);
const PackagePage = lazyPage(() => import('@/features/packages/PackagePage'), 'PackagePage');
const TemplatesTab = lazyPage(() => import('@/features/packages/TemplatesTab'), 'TemplatesTab');
import type { NewBookingSearch } from '@/features/scheduling/BookingNewPage';
import { RECORD_TABS, type RecordTab } from '@/features/patients/recordTabs';
const BookingNewPage = lazyPage(
  () => import('@/features/scheduling/BookingNewPage'),
  'BookingNewPage',
);
const BookingDetailPage = lazyPage(
  () => import('@/features/scheduling/BookingDetailPage'),
  'BookingDetailPage',
);
const ReschedulePage = lazyPage(
  () => import('@/features/scheduling/ReschedulePage'),
  'ReschedulePage',
);
const ProgressPage = lazyPage(() => import('@/features/progress/ProgressPage'), 'ProgressPage');
const InstrumentStartPage = lazyPage(
  () => import('@/features/instruments/InstrumentStartPage'),
  'InstrumentStartPage',
);
const InstrumentAdministrationPage = lazyPage(
  () => import('@/features/instruments/InstrumentAdministrationPage'),
  'InstrumentAdministrationPage',
);
const InstrumentSeriesPage = lazyPage(
  () => import('@/features/instruments/InstrumentSeriesPage'),
  'InstrumentSeriesPage',
);
const TimelinePage = lazyPage(() => import('@/features/progress/TimelinePage'), 'TimelinePage');
const CalendarPage = lazyPage(() => import('@/features/scheduling/CalendarPage'), 'CalendarPage');
const DashboardPage = lazyPage(() => import('@/features/reports/DashboardPage'), 'DashboardPage');
const GroupSessionNewPage = lazyPage(
  () => import('@/features/groups/GroupSessionNewPage'),
  'GroupSessionNewPage',
);
const GroupSessionPage = lazyPage(
  () => import('@/features/groups/GroupSessionPage'),
  'GroupSessionPage',
);
const LiveBookingPage = lazyPage(
  () => import('@/features/live/LiveSessionPage'),
  'LiveBookingPage',
);
const LiveGroupPage = lazyPage(() => import('@/features/live/LiveSessionPage'), 'LiveGroupPage');
const SmartPlanPage = lazyPage(
  () => import('@/features/scheduling/SmartPlanPage'),
  'SmartPlanPage',
);
const RecordDeliveryPage = lazyPage(
  () => import('@/features/sessions/RecordDeliveryPage'),
  'RecordDeliveryPage',
);
const SessionDetailPage = lazyPage(
  () => import('@/features/sessions/SessionDetailPage'),
  'SessionDetailPage',
);
const PaymentsWorkspace = lazyPage(
  () => import('@/features/payments/PaymentsWorkspace'),
  'PaymentsWorkspace',
);
const RecordPaymentPage = lazyPage(
  () => import('@/features/payments/RecordPaymentPage'),
  'RecordPaymentPage',
);
const PaymentDetailPage = lazyPage(
  () => import('@/features/payments/PaymentDetailPage'),
  'PaymentDetailPage',
);
const SessionReportsPage = lazyPage(
  () => import('@/features/sessions/SessionReportsPage'),
  'SessionReportsPage',
);
const PatientPaymentsPage = lazyPage(
  () => import('@/features/payments/PatientPaymentsPage'),
  'PatientPaymentsPage',
);
import { ClinicEntryPage } from '@/features/entry/ClinicEntryPage';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { PERM, type PermissionKey } from '@/core/authz/permissions';
import { useIntl } from 'react-intl';
import { Button, Notice, Skeleton } from '@/ui/kit';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

/**
 * ZOY-614: the ONE path that carries a slug. It is public — a signed-out
 * visitor must reach it — and it decides for itself what to do with the
 * session it finds, so it is not wrapped in PublicOnly.
 */
const clinicEntryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/c/$slug',
  component: ClinicEntryPage,
});

const signinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin',
  component: () => (
    <PublicOnly>
      <SignInPage />
    </PublicOnly>
  ),
});

const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot',
  component: () => (
    <PublicOnly>
      <ForgotPasswordPage />
    </PublicOnly>
  ),
});

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  component: AuthedLayout,
});

// ZOY-315: the catch-all — any path no route owns renders Not Found INSIDE
// the authenticated shell (signed-out visitors hit the normal sign-in
// redirect first). No route or debug detail is exposed.
const notFoundSplatRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '$',
  component: NotFoundPage,
});

/** ZOY-315: an unmatched URL lands inside the normal shell with a way back. */
function NotFoundPage() {
  const intl = useIntl();
  return (
    <Notice
      kind="info"
      title={intl.formatMessage({ id: 'error.notFoundTitle' })}
      action={
        <Link to="/today">
          <Button variant="brand">{intl.formatMessage({ id: 'error.notFoundCta' })}</Button>
        </Link>
      }
    >
      {intl.formatMessage({ id: 'error.notFoundBody' })}
    </Notice>
  );
}

/** Arrived-by-link into an area the role may never open: explain, don't tease. */
function Guarded({ perm, children }: { perm: PermissionKey; children: React.ReactNode }) {
  const authz = useAuthz();
  const intl = useIntl();
  if (authz.state.status === 'ready' && !authz.can(perm)) {
    return (
      <Notice kind="info" title={intl.formatMessage({ id: 'error.forbiddenTitle' })}>
        {intl.formatMessage({ id: 'error.forbiddenBody' })}
      </Notice>
    );
  }
  return <>{children}</>;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => <Navigate to="/home" />,
});

/**
 * ZOY-702: the default landing, decided by authorization once it is known —
 * the Dashboard for whoever may open it (`clinic.reports.read`, which Full
 * Clinic Administrator satisfies), Today for everyone else. It renders inside
 * the authed layout, so the permission snapshot is ready by then; a
 * remembered deep link still wins, because it never comes through here.
 */
function HomeRedirect() {
  const authz = useAuthz();
  return <Navigate to={landingFor(authz.can)} replace />;
}
const homeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/home',
  component: HomeRedirect,
});

/** The page itself sends a reader without the key on to Today — it never renders refused. */
const reportsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/reports',
  validateSearch: (q: Record<string, unknown>): DashboardSearch => ({
    from: pick(q.from),
    to: pick(q.to),
  }),
  component: DashboardPage,
});

const todayRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/today',
  component: TodayPage,
});

const accountRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/account',
  component: AccountPage,
});

// The user guides — for everyone signed in, no permission key.
const helpRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/help',
  component: ManualsPage,
});

const changePasswordRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/account/change-password',
  component: ChangePasswordPage,
});

const pick = (v: unknown, allowed?: readonly string[]): string | undefined =>
  typeof v === 'string' && v !== '' && (!allowed || allowed.includes(v)) ? v : undefined;

const calendarRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/calendar',
  component: () => (
    <Guarded perm={PERM.bookingsRead}>
      <Outlet />
    </Guarded>
  ),
});
const calendarIndexRoute = createRoute({
  getParentRoute: () => calendarRoute,
  path: '/',
  validateSearch: (q: Record<string, unknown>): CalendarSearch => ({
    view: pick(q.view, ['day', 'week', 'list']) as CalendarSearch['view'],
    date: pick(q.date),
    lens: pick(q.lens, ['practitioner', 'room']) as CalendarSearch['lens'],
    pr: pick(q.pr),
    room: pick(q.room),
    svc: pick(q.svc),
    st: pick(q.st, ['booked', 'all', 'cancelled', 'no_show']) as CalendarSearch['st'],
    mode: pick(q.mode, ['in_clinic', 'off_site', 'online']),
    pt: pick(q.pt),
    range: pick(q.range, ['day', 'week', 'month']) as CalendarSearch['range'],
    // The session sheet: route-backed so Back closes it and a session can be linked.
    booking: pick(q.booking),
  }),
  component: CalendarPage,
});
const bookingNewRoute = createRoute({
  getParentRoute: () => calendarRoute,
  path: '/new',
  validateSearch: (q: Record<string, unknown>): NewBookingSearch => ({
    patientId: pick(q.patientId),
    pr: pick(q.pr),
    room: pick(q.room),
    date: pick(q.date),
    time: pick(q.time),
    svc: pick(q.svc),
  }),
  component: () => (
    <Guarded perm={PERM.bookingsSchedule}>
      <BookingNewPage />
    </Guarded>
  ),
});
const bookingRoute = createRoute({
  getParentRoute: () => calendarRoute,
  path: '/bookings/$bookingId',
  component: BookingDetailPage,
});
const bookingDeliverRoute = createRoute({
  getParentRoute: () => calendarRoute,
  path: '/bookings/$bookingId/deliver',
  component: () => (
    <Guarded perm={PERM.sessionsDeliver}>
      <RecordDeliveryPage />
    </Guarded>
  ),
});
const sessionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sessions/$sessionId',
  component: () => (
    <Guarded perm={PERM.sessionsRead}>
      <SessionDetailPage />
    </Guarded>
  ),
});
const patientPaymentsRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/$patientId/payments',
  component: () => (
    <Guarded perm={PERM.sessionsRead}>
      <PatientPaymentsPage />
    </Guarded>
  ),
});
// The record's session reports read end to end — the same key that opens a session.
const sessionReportsRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/$patientId/session-reports',
  validateSearch: (q: Record<string, unknown>): SessionReportsSearch => ({
    service: pick(q.service),
    by: pick(q.by),
    from: pick(q.from),
    to: pick(q.to),
    order: pick(q.order, ['oldest']) as SessionReportsSearch['order'],
    voided: pick(q.voided, ['1']) as SessionReportsSearch['voided'],
  }),
  component: () => (
    <Guarded perm={PERM.sessionsRead}>
      <SessionReportsPage />
    </Guarded>
  ),
});
// ZOY-685: a group occurrence — created from the calendar like any event,
// managed on its own page. ZOY-696: the hosted video workspace lives INSIDE
// the app, behind the delivery key like the staff door itself.
const groupNewRoute = createRoute({
  getParentRoute: () => calendarRoute,
  path: '/groups/new',
  validateSearch: (q: Record<string, unknown>): GroupNewSearch => ({
    date: pick(q.date),
    time: pick(q.time),
    pr: pick(q.pr),
    svc: pick(q.svc),
    room: pick(q.room),
  }),
  component: () => (
    <Guarded perm={PERM.bookingsSchedule}>
      <GroupSessionNewPage />
    </Guarded>
  ),
});
const groupRoute = createRoute({
  getParentRoute: () => calendarRoute,
  path: '/groups/$groupId',
  component: GroupSessionPage,
});
const groupLiveRoute = createRoute({
  getParentRoute: () => calendarRoute,
  path: '/groups/$groupId/live',
  component: () => (
    <Guarded perm={PERM.sessionsDeliver}>
      <LiveGroupPage />
    </Guarded>
  ),
});
const bookingLiveRoute = createRoute({
  getParentRoute: () => calendarRoute,
  path: '/bookings/$bookingId/live',
  component: () => (
    <Guarded perm={PERM.sessionsDeliver}>
      <LiveBookingPage />
    </Guarded>
  ),
});
const bookingRescheduleRoute = createRoute({
  getParentRoute: () => calendarRoute,
  path: '/bookings/$bookingId/reschedule',
  component: () => (
    <Guarded perm={PERM.bookingsReschedule}>
      <ReschedulePage />
    </Guarded>
  ),
});

const patientsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/patients',
  component: () => (
    <Guarded perm={PERM.patientsRead}>
      <Outlet />
    </Guarded>
  ),
});
const patientsIndexRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/',
  component: PatientsListPage,
});
const patientNewRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/new',
  component: () => (
    <Guarded perm="clinic.patients.register">
      <PatientRegisterPage />
    </Guarded>
  ),
});
const patientResolveRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/resolve',
  component: ResolutionPreviewPage,
});
/** ZOY workspace: `?tab=` picks the primary tab; unknown → Overview. */
export interface PatientRecordSearch {
  tab?: RecordTab;
}
const patientRecordRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/$patientId',
  validateSearch: (q: Record<string, unknown>): PatientRecordSearch => {
    const tab = pick(q.tab, RECORD_TABS);
    return tab === undefined ? {} : { tab: tab as RecordTab };
  },
  component: PatientRecordPage,
});
const patientEditRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/$patientId/edit',
  component: () => (
    <Guarded perm="clinic.patients.update">
      <PatientEditFormPage />
    </Guarded>
  ),
});
/** ZOY-229: the explicit registration act for a Guest (same form, register mode). */
const patientCompleteRegistrationRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/$patientId/register',
  component: () => (
    <Guarded perm="clinic.patients.register">
      <PatientCompleteRegistrationPage />
    </Guarded>
  ),
});
const patientIntakeRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/$patientId/intake/$intakeId',
  component: () => (
    <Guarded perm="clinic.intake.read">
      <IntakePage />
    </Guarded>
  ),
});

const mk = <P extends string>(path: P, perm: PermissionKey, C: () => JSX.Element) =>
  createRoute({
    getParentRoute: () => patientsRoute,
    path,
    component: () => (
      <Guarded perm={perm}>
        <C />
      </Guarded>
    ),
  });
/**
 * ZOY-599: clinical instruments live on the PATIENT record — never under a
 * booking, session or assessment. Static segments ('new', 'series') rank
 * above the dynamic administration id.
 */
const instrumentNewRoute = mk(
  '/$patientId/instruments/new',
  PERM.instrumentsAdminister,
  InstrumentStartPage,
);
const instrumentSeriesRoute = mk(
  '/$patientId/instruments/series',
  PERM.instrumentsRead,
  InstrumentSeriesPage,
);
const instrumentRoute = mk(
  '/$patientId/instruments/$administrationId',
  PERM.instrumentsRead,
  InstrumentAdministrationPage,
);
const progressRoute = mk('/$patientId/progress', PERM.patientsRead, ProgressPage);
const timelineRoute = mk('/$patientId/progress/timeline', PERM.patientsRead, TimelinePage);

const assessmentNewRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/$patientId/assessments/new',
  validateSearch: (
    q: Record<string, unknown>,
  ): { type?: 'initial' | 'reassessment'; bookingId?: string } => ({
    type: pick(q.type, ['initial', 'reassessment']) as 'initial' | 'reassessment' | undefined,
    // ZOY-552: arriving from a started booked assessment session.
    bookingId: typeof q.bookingId === 'string' ? q.bookingId : undefined,
  }),
  component: () => (
    <Guarded perm={PERM.assessmentsWrite}>
      <AssessmentNewPage />
    </Guarded>
  ),
});
const assessmentRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/$patientId/assessments/$assessmentId',
  component: () => (
    <Guarded perm={PERM.assessmentsRead}>
      <AssessmentPage />
    </Guarded>
  ),
});
const packageNewRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/$patientId/packages/new',
  validateSearch: (search: Record<string, unknown>): { from?: string } =>
    typeof search.from === 'string' && search.from !== '' ? { from: search.from } : {},
  component: () => (
    <Guarded perm={PERM.packagesManage}>
      <PackageNewPage />
    </Guarded>
  ),
});
const packagePlanRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/$patientId/packages/$packageId/plan',
  validateSearch: (q: Record<string, unknown>): { item?: string } => ({ item: pick(q.item) }),
  component: () => (
    <Guarded perm={PERM.bookingsSchedule}>
      <SmartPlanPage />
    </Guarded>
  ),
});
const packageRoute = createRoute({
  getParentRoute: () => patientsRoute,
  path: '/$patientId/packages/$packageId',
  component: () => (
    <Guarded perm={PERM.packagesRead}>
      <PackagePage />
    </Guarded>
  ),
});

/** ZOY-665: the clinic-wide family workspace — a top-level destination under families.read. */
const FamilyWorkspacePage = lazyPage(
  () => import('@/features/family/FamilyWorkspacePage'),
  'FamilyWorkspacePage',
);
/** ZOY-712: Follow-ups (the Clinic CRM) — reception's worklist, its own destination under families.read. */
const FollowUpsPage = lazyPage(() => import('@/features/followups/FollowUpsPage'), 'FollowUpsPage');
const followUpsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/follow-ups',
  validateSearch: (q: Record<string, unknown>): FollowUpsSearch => ({
    tab: pick(q.tab, ['open', 'closed', 'continuation']) as FollowUpsSearch['tab'],
    who: pick(q.who, ['mine', 'all']) as FollowUpsSearch['who'],
    due: pick(q.due, ['overdue', 'today', 'week', 'none']) as FollowUpsSearch['due'],
    linked: pick(q.linked, ['none', 'guest', 'registered']) as FollowUpsSearch['linked'],
    req: pick(q.req, ['1', '0']) as FollowUpsSearch['req'],
    q: pick(q.q),
    record: pick(q.record),
    new: pick(q.new, ['1']) as FollowUpsSearch['new'],
    phone: pick(q.phone),
  }),
  component: () => (
    <Guarded perm={PERM.familiesRead}>
      <FollowUpsPage />
    </Guarded>
  ),
});

const familyRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/family',
  validateSearch: (q: Record<string, unknown>): FamilySearch => ({
    tab: pick(q.tab, ['links', 'requests']) as FamilySearch['tab'],
    pt: pick(q.pt),
  }),
  component: () => (
    <Guarded perm={PERM.familiesRead}>
      <FamilyWorkspacePage />
    </Guarded>
  ),
});

const paymentsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/payments',
  component: () => (
    <Guarded perm={PERM.sessionsRead}>
      <Outlet />
    </Guarded>
  ),
});
const paymentsIndexRoute = createRoute({
  getParentRoute: () => paymentsRoute,
  path: '/',
  validateSearch: (q: Record<string, unknown>): PaymentsSearch => ({
    tab: pick(q.tab, ['unpaid', 'history', 'balances', 'received']) as PaymentsSearch['tab'],
    pt: pick(q.pt),
    from: pick(q.from),
    to: pick(q.to),
    svc: pick(q.svc),
    pr: pick(q.pr),
    method: pick(q.method),
    status: pick(q.status, ['recorded', 'reversed']) as PaymentsSearch['status'],
  }),
  component: PaymentsWorkspace,
});
const paymentSessionRoute = createRoute({
  getParentRoute: () => paymentsRoute,
  path: '/sessions/$sessionId',
  component: PaymentDetailPage,
});
const paymentRecordRoute = createRoute({
  getParentRoute: () => paymentsRoute,
  path: '/sessions/$sessionId/record',
  component: () => (
    <Guarded perm={PERM.paymentRecord}>
      <RecordPaymentPage />
    </Guarded>
  ),
});

const clinicSetupRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/clinic-setup',
  component: () => (
    <Guarded perm={PERM.settingsRead}>
      <SetupLayout />
    </Guarded>
  ),
});

const setupIndexRoute = createRoute({
  getParentRoute: () => clinicSetupRoute,
  path: '/',
  component: ProfileTab,
});
const setupStaffRoute = createRoute({
  getParentRoute: () => clinicSetupRoute,
  path: '/staff',
  component: () => (
    <Guarded perm={SETUP_SURFACES.staff}>
      <StaffTab />
    </Guarded>
  ),
});
const setupStaffNewRoute = createRoute({
  getParentRoute: () => clinicSetupRoute,
  path: '/staff/new',
  component: () => (
    <Guarded perm={SETUP_SURFACES.staff}>
      <StaffCreatePage />
    </Guarded>
  ),
});
const setupMemberRoute = createRoute({
  getParentRoute: () => clinicSetupRoute,
  path: '/staff/$uid',
  validateSearch: (q: Record<string, unknown>): MemberSearch => ({
    tab: pick(q.tab, MEMBER_TABS) as MemberSearch['tab'],
  }),
  component: () => (
    <Guarded perm={SETUP_SURFACES.staff}>
      <MemberPage />
    </Guarded>
  ),
});
const setupRolesRoute = createRoute({
  getParentRoute: () => clinicSetupRoute,
  path: '/roles',
  component: () => (
    <Guarded perm={SETUP_SURFACES.roles}>
      <RolesTab />
    </Guarded>
  ),
});
const setupHoursRoute = createRoute({
  getParentRoute: () => clinicSetupRoute,
  path: '/hours',
  component: () => (
    <Guarded perm={SETUP_SURFACES.hours}>
      <HoursTab />
    </Guarded>
  ),
});
const setupRoomsRoute = createRoute({
  getParentRoute: () => clinicSetupRoute,
  path: '/rooms',
  component: () => (
    <Guarded perm={SETUP_SURFACES.rooms}>
      <RoomsTab />
    </Guarded>
  ),
});
const setupServicesRoute = createRoute({
  getParentRoute: () => clinicSetupRoute,
  path: '/services',
  component: () => (
    <Guarded perm={SETUP_SURFACES.services}>
      <ServicesTab />
    </Guarded>
  ),
});
const setupCatalogsRoute = createRoute({
  getParentRoute: () => clinicSetupRoute,
  path: '/catalogs',
  component: () => (
    <Guarded perm={SETUP_SURFACES.catalogs}>
      <CatalogsTab />
    </Guarded>
  ),
});
const setupExtraChargesRoute = createRoute({
  getParentRoute: () => clinicSetupRoute,
  path: '/extra-charges',
  component: () => (
    <Guarded perm={SETUP_SURFACES.extraCharges}>
      <ExtraChargesTab />
    </Guarded>
  ),
});
const setupTemplatesRoute = createRoute({
  getParentRoute: () => clinicSetupRoute,
  path: '/templates',
  component: () => (
    <Guarded perm={SETUP_SURFACES.templates}>
      <TemplatesTab />
    </Guarded>
  ),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  clinicEntryRoute,
  signinRoute,
  forgotRoute,
  authedRoute.addChildren([
    homeRoute,
    reportsRoute,
    todayRoute,
    accountRoute,
    helpRoute,
    changePasswordRoute,
    calendarRoute.addChildren([
      calendarIndexRoute,
      bookingNewRoute,
      bookingRoute,
      bookingRescheduleRoute,
      bookingDeliverRoute,
      bookingLiveRoute,
      groupNewRoute,
      groupRoute,
      groupLiveRoute,
    ]),
    sessionsRoute,
    familyRoute,
    followUpsRoute,
    patientsRoute.addChildren([
      patientsIndexRoute,
      patientNewRoute,
      patientResolveRoute,
      patientRecordRoute,
      patientEditRoute,
      patientCompleteRegistrationRoute,
      patientIntakeRoute,
      assessmentNewRoute,
      assessmentRoute,
      packageNewRoute,
      packagePlanRoute,
      packageRoute,
      patientPaymentsRoute,
      sessionReportsRoute,
      instrumentNewRoute,
      instrumentSeriesRoute,
      instrumentRoute,
      progressRoute,
      timelineRoute,
    ]),
    paymentsRoute.addChildren([paymentsIndexRoute, paymentSessionRoute, paymentRecordRoute]),
    clinicSetupRoute.addChildren([
      setupIndexRoute,
      setupStaffRoute,
      setupStaffNewRoute,
      setupMemberRoute,
      setupRolesRoute,
      setupHoursRoute,
      setupRoomsRoute,
      setupServicesRoute,
      setupCatalogsRoute,
      setupExtraChargesRoute,
      setupTemplatesRoute,
    ]),
    notFoundSplatRoute,
  ]),
]);

export const router = createRouter({ routeTree, defaultNotFoundComponent: NotFoundPage });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
